import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Printer, X, Download, ShieldCheck, Building2, User2 } from "lucide-react";
import { cn } from "../ui/utils";
import { Separator } from "../ui/separator";

interface InvoiceModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  account: {
    id: string;
    leadName: string;
    course: string;
    totalFees: number;
    paidAmount: number;
    balanceAmount: number;
    paymentStatus: string;
    lastPaymentDate: string;
    dueDate: string;
    counselor: { name: string };
  } | null;
}

export function InvoiceDetailModal({ isOpen, onOpenChange, account }: InvoiceModalProps) {
  if (!account) return null;

  const handlePrint = () => {
    window.print();
  };

  const invoiceId = `INV-${account.id.slice(0, 8).toUpperCase()}`;
  const currentDate = new Date().toLocaleDateString('en-GB');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-white dark:bg-slate-900 border-none shadow-2xl">
        {/* Style for Print Optimization */}
        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            body * { visibility: hidden; }
            .printable-invoice, .printable-invoice * { visibility: visible; }
            .printable-invoice { 
              position: absolute; 
              left: 0; 
              top: 0; 
              width: 100% !important; 
              height: auto !important; 
              padding: 0 !important; 
              margin: 0 !important;
              background: white !important;
              color: black !important;
            }
            .no-print { display: none !important; }
            @page { size: A4; margin: 20mm; }
            .invoice-card { border: none !important; box-shadow: none !important; }
            .bg-slate-50 { background-color: #f8fafc !important; }
            .text-primary { color: #2563eb !important; }
          }
        `}} />

        <div className="flex flex-col h-[90vh] md:h-auto max-h-[95vh]">
          {/* Header (No Print) */}
          <div className="flex items-center justify-between p-4 pr-12 border-b border-border/50 bg-muted/20 no-print">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <span className="font-bold text-sm tracking-tight">Invoice Transaction Node</span>
            </div>
            <div className="flex items-center gap-2">
               <Button variant="outline" size="sm" onClick={handlePrint} className="h-8 gap-2 border-border">
                <Printer className="h-3.5 w-3.5" /> Print Documents
              </Button>
            </div>
          </div>

          {/* Printable Invoice Area */}
          <div className="printable-invoice flex-1 overflow-y-auto p-10 bg-white dark:bg-slate-900">
            <div className="max-w-3xl mx-auto space-y-12">
              
              {/* Branded Header */}
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <h1 className="text-3xl font-black tracking-tighter text-slate-950 dark:text-white uppercase">XEROALPHA</h1>
                  <p className="text-[10px] font-bold text-slate-500 tracking-[0.3em] uppercase">Academic Solutions Global</p>
                  <div className="pt-6 space-y-1 text-xs text-muted-foreground font-medium">
                    <p>Financial Operations Division</p>
                    <p>Sector V, Salt Lake, BL-90</p>
                    <p>Kolkata, WB 700091</p>
                  </div>
                </div>
                <div className="text-right space-y-2">
                  <div className="inline-block px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded font-mono text-xs font-bold text-slate-900 dark:text-slate-300">
                    TAX INVOICE
                  </div>
                  <div className="space-y-1">
                    <p className="text-2xl font-black text-slate-900 dark:text-white uppercase">{invoiceId}</p>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Dated: {currentDate}</p>
                  </div>
                </div>
              </div>

              {/* Identity Section */}
              <div className="grid grid-cols-2 gap-12">
                <div className="space-y-4">
                  <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-800 pb-1">Billed Recipient</h3>
                  <div className="space-y-1">
                    <p className="text-base font-bold text-slate-900 dark:text-white">{account.leadName}</p>
                    <p className="text-xs text-muted-foreground">Course Enrolled: <span className="text-foreground font-bold">{account.course}</span></p>
                    <p className="text-xs text-muted-foreground">Internal Ref: #{account.id.slice(-6).toUpperCase()}</p>
                  </div>
                </div>
                <div className="space-y-4 text-right">
                  <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-800 pb-1">Counselor Attribution</h3>
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{account.counselor.name}</p>
                    <p className="text-xs text-muted-foreground italic">Authorized Academic Liaison</p>
                  </div>
                </div>
              </div>

              {/* Financial Ledger Table */}
              <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Description of Service</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Amount (INR)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    <tr>
                      <td className="px-6 py-8">
                        <p className="text-sm font-bold text-slate-900 dark:text-white">{account.course} - Professional Tuition Fees</p>
                        <p className="text-[10px] text-muted-foreground mt-1 max-w-sm">
                          Standard academic enrollment fees including resource access, certification modules, and placement support ecosystem.
                        </p>
                      </td>
                      <td className="px-6 py-8 text-right font-mono text-sm font-bold tabular-nums">
                        ₹{account.totalFees.toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50/50 dark:bg-slate-900">
                      <td className="px-6 py-4 text-right text-xs font-bold text-muted-foreground uppercase tracking-widest">Subtotal Aggregate</td>
                      <td className="px-6 py-4 text-right font-mono text-sm font-bold tabular-nums">₹{account.totalFees.toLocaleString()}</td>
                    </tr>
                    <tr className="bg-green-50/20 dark:bg-green-500/5">
                      <td className="px-6 py-4 text-right text-xs font-extrabold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Total Amount Received</td>
                      <td className="px-6 py-4 text-right font-mono text-sm font-black tabular-nums text-emerald-600 dark:text-emerald-400">
                        - ₹{account.paidAmount.toLocaleString()}
                      </td>
                    </tr>
                    <tr className="bg-slate-950 dark:bg-white text-white dark:text-slate-950">
                      <td className="px-6 py-6 text-right text-xs font-black uppercase tracking-[0.2em]">Net Outstanding Balance</td>
                      <td className="px-6 py-6 text-right font-mono text-xl font-black tabular-nums">
                        ₹{account.balanceAmount.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Verification Stamp Placeholder */}
              <div className="flex justify-between items-end pt-8">
                <div className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest">Payment Due Date</p>
                    <p className="text-xs font-black text-slate-900 dark:text-white">{account.dueDate}</p>
                  </div>
                  <div className="h-16 w-32 border-2 border-slate-100 dark:border-slate-800 rounded-lg flex items-center justify-center relative opacity-20 no-print">
                      <div className="absolute inset-0 flex items-center justify-center border border-slate-100/50 rounded-lg rotate-[-12deg]">
                        <span className="text-[10px] font-black uppercase tracking-tighter opacity-30">OFFICIAL STAMP</span>
                      </div>
                  </div>
                </div>
                
                <div className="text-right space-y-6">
                   <div className="space-y-1">
                    <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest">Authorized Signatory</p>
                     <p className="text-xs font-bold font-serif italic text-slate-900 dark:text-white pt-4 border-b border-slate-900 dark:border-white w-48 ml-auto">
                        Finance Controller Node
                     </p>
                  </div>
                </div>
              </div>

              {/* Professional Footer */}
              <div className="border-t border-slate-100 dark:border-slate-800 pt-8 space-y-2">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Legal Notice & Disclosure</p>
                <p className="text-[8px] text-slate-400 leading-relaxed uppercase font-medium">
                  This document is a computer-generated transaction record and does not require a physical signature for validity under the IT Act 2000. All fees mentioned are exclusive of platform processing surcharges where applicable. Total fees once paid are non-refundable as per the corporate refund policy v4.2.
                </p>
              </div>

            </div>
          </div>

          {/* Footer (No Print) */}
          <div className="p-6 border-t border-border/50 bg-muted/20 flex justify-end gap-3 no-print">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl px-8 font-bold">Close Portal</Button>
            <Button onClick={handlePrint} className="rounded-xl px-8 font-bold shadow-lg shadow-primary/20 bg-primary">
              <Printer className="h-4 w-4 mr-2" /> Dispatch to Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
